import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery, useRemoveQueries } from '@/hooks';
import type { z } from 'zod';
import { queryKeyMap } from '../../utils/query-keys';
import {
  ReservedProjectModel,
  type ReservedProjectUpsertSchema,
  ReservedProjectsModel,
} from '../_models/reserved-project';

const API_BASE_URL = '/api/reserved-project-codes/';

export type UseReservedProjectsParams = {
  ordering?: string;
};

export const useReservedProjects = ({
  params,
}: {
  params?: UseReservedProjectsParams;
} = {}) => {
  return useQuery({
    queryKey: queryKeyMap.reservedProjects(params),
    queryFn: async () => {
      const data = await axiosGet(API_BASE_URL, params);
      return ReservedProjectsModel.parse(data);
    },
  });
};

export const useReservedProject = (id: string) => {
  return useQuery({
    queryKey: queryKeyMap.reservedProject(id),
    queryFn: async () => {
      const data = await axiosGet(`${API_BASE_URL}${id}/`);
      return ReservedProjectModel.parse(data);
    },
    enabled: !!id,
  });
};

export const useCreateReservedProject = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (
      payload: z.infer<typeof ReservedProjectUpsertSchema>,
    ) => {
      const data = await axiosPost(API_BASE_URL, payload);
      return ReservedProjectModel.parse(data);
    },
    onSuccess: async () => {
      await invalidate(queryKeyMap.reservedProjects());
    },
  });
};

export const useUpdateReservedProject = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: z.infer<typeof ReservedProjectUpsertSchema> & { id: string }) => {
      const data = await axiosPatch(`${API_BASE_URL}${id}/`, payload);
      return ReservedProjectModel.parse(data);
    },
    onSuccess: async (_, { id }) => {
      await Promise.all([
        invalidate(queryKeyMap.reservedProjects()),
        invalidate(queryKeyMap.reservedProject(id)),
      ]);
    },
  });
};

export const useDeleteReservedProject = () => {
  const { invalidate } = useInvalidateQuery();
  const { removeQuery } = useRemoveQueries();

  return useMutation({
    mutationFn: (id: string) => axiosDelete(`${API_BASE_URL}${id}/`),
    onSuccess: async (_, id) => {
      await invalidate(queryKeyMap.reservedProjects());
      removeQuery(queryKeyMap.reservedProject(id));
    },
  });
};
